import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIntl, injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { UniversalLink, PreviewImage } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import { Grid, Container, Input, Card } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faClock } from '@fortawesome/free-regular-svg-icons';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import "dayjs/locale/ca";
import { isEmpty, map } from 'lodash';
import { Icon } from '../../';
import { formatDate, formatTime } from '../../../helpers'


const messages = defineMessages({
  next: {
    id: 'Next',
    defaultMessage: 'Next',
  },
  previous: {
    id: 'Previous',
    defaultMessage: 'Previous',
  },
  today: {
    id: 'Today',
    defaultMessage: 'Today',
  },
  month: {
    id: 'Month',
    defaultMessage: 'Month',
  },
  week: {
    id: 'Week',
    defaultMessage: 'Week',
  },
  day: {
    id: 'Day',
    defaultMessage: 'Day',
  },
  agenda: {
    id: 'Agenda',
    defaultMessage: 'Agenda',
  },
  date: {
    id: 'Date',
    defaultMessage: 'Date',
  },
  time: {
    id: 'Time',
    defaultMessage: 'Time',
  },
  event: {
    id: 'Event',
    defaultMessage: 'Event',
  },
  showMore: {
    id: 'showMore',
    defaultMessage: '+{count} more',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
});


const View = (props) => {
  const { data } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const events = useSelector((state) => state.search.subrequests.events?.items);
  const eventsCalendar = useSelector((state) => state.search.subrequests.eventsCalendar?.items);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchText, setSearchText] = useState('');
  const vocabularySubjects = 'plone.app.vocabularies.Keywords';
  const itemsSubjects = useSelector(
    (state) =>
      state.vocabularies[vocabularySubjects] && state.vocabularies[vocabularySubjects].items
        ? [...state.vocabularies[vocabularySubjects].items]
        : [],
    shallowEqual,
  );

  const onSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchText.trim() !== '') {
        const url = `${eventsURL}?searchableText=${encodeURIComponent(searchText.trim())}`;
        window.location.href = url;
      }
    }
  };

  const mappedEvents = eventsCalendar?.map((event) => ({
    title: event.title,
    start: new Date(event.start),
    end: new Date(event.end),
    allDay: false,
    resource: event,
  }));

  let href = (data.linkHref?.[0]?.['@id'] ) || '';
  let link = href ? flattenToAppURL(href) : '';
  dayjs.locale('ca');
  const localizer = dayjsLocalizer(dayjs);

  const calendarComponents = {
    dateCellWrapper: ({ children, value }) => {
      const day = dayjs(value).format('DD-MM-YYYY');
      const hasEvents = mappedEvents?.some(event =>
        dayjs(value).isBetween(dayjs(event.start), dayjs(event.end), 'day', '[]')
      );

      if (!hasEvents) return children;

      const dayUrl = `${flattenToAppURL(data.folder?.[0]?.['@id'] || '/')}?date=${day}`;
      return React.cloneElement(children, {
        className: `${children.props.className || ''} rbc-day-has-events`,
        onClick: () => { window.location.href = dayUrl; },
        style: { cursor: 'pointer' },
      });
    },
    dateHeader: ({ date, label }) => {
      const hasEvents = mappedEvents?.some(event =>
        dayjs(date).isBetween(dayjs(event.start), dayjs(event.end), 'day', '[]')
      );

      const day = dayjs(date).format('DD-MM-YYYY');
      const dayUrl = `${flattenToAppURL(data.folder?.[0]?.['@id'] || '/')}?date=${day}`;

      if (!hasEvents) return <span>{label}</span>;

      return (
        <UniversalLink href={dayUrl} className="rbc-day-has-events">
          {label}
        </UniversalLink>
      );
    },
    event: () => null
  }

  const getEventsURL = function () {
    let path = data.folder?.[0]?.['@id'] || '/'
    return flattenToAppURL(path)
  }
  const eventsURL = getEventsURL()

  const onCalendarMonthChange = (data) => {
    setStartDate(dayjs(data).startOf('month'));
    setEndDate(dayjs(data).endOf('month'));
  }

  const getEventSubject = function (subject) {
    if (!itemsSubjects) {
      return subject
    }
    for (let item of itemsSubjects) {
      if (item.value === subject) return item.label
    }
    return subject
  }

  const getEventDate = function (start, end, open_end) {
    const optionsFullTime = { year: 'numeric', month: '2-digit', day: '2-digit' };

    if (open_end) {
    return (
      <time dateTime={start} className='d-flex align-items-center'>
      <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
      <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
      </time>
    );
    }

    const startDay = formatDate(start);
    const endDay = formatDate(end);

    if (startDay === endDay) {
      return (
        <time dateTime={start} className='d-flex align-items-center'>
          <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
          <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
        </time>
      );
    }

    return (
      <>
        <time suppressHydrationWarning dateTime={start} className='d-flex align-items-center mb-1'>
          <span className="eventsItem__date__day">
            <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
            <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
          </span>
        </time >
        <time suppressHydrationWarning dateTime={end} className='d-flex align-items-center'>
          <span className="eventsItem__date__day">
            <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
            <span suppressHydrationWarning>{formatDate(end, optionsFullTime)}</span>
          </span>
        </time>
      </>
    );
  };

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: vocabularySubjects }));
  }, [dispatch]);

  useEffect(() => {
    let contentFilter = {
      portal_type: ['Event'], 
      review_state: 'published',
      sort_on: 'start',
      metadata_fields: ['start', 'end', 'open_end', 'whole_day', 'location', 'Subject']
    };
    const now = dayjs().format('YYYY/MM/DD HH:mm');
    contentFilter['start.query'] = [now];
    contentFilter['start.range'] = 'min';
    dispatch(
      searchContent(
        eventsURL, 
        contentFilter,
        'events',
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    let contentFilter = {
      portal_type: ['Event'], 
      review_state: 'published',
      sort_on: 'start',
      metadata_fields: ['start', 'end'],
    };

    const calendarStart = (startDate || dayjs().startOf('month')).startOf('week');
    const calendarEnd = (endDate || dayjs().endOf('month')).endOf('week');
    contentFilter['start.query'] = [calendarStart.format('YYYY/MM/DD') + ' 00:00', calendarEnd.format('YYYY/MM/DD') + ' 23:59'];
    contentFilter['start.range'] = 'min:max';

    dispatch(
      searchContent(
        eventsURL, 
        contentFilter,
        'eventsCalendar',
      ),
    );
  }, [dispatch, startDate, endDate]);

  return (
    <div className="stretch">
      <section className='agendaBlock'>
          <Container className=''>
              <div className="agendaBlock__header">
                  {data.title && <h2 className="agendaBlock__title">{data.title}</h2>}
                  <label className='visually-hidden' htmlFor="agendaBlockInputSearch"><FormattedMessage id="Cercar a l'agenda" defaultMessage="Cercar a l'agenda" /></label>
                  <Input
                    id='agendaBlockInputSearch'
                    className='agendaBlock__search d-print-none'
                    icon='search'
                    iconPosition='left'
                    placeholder={intl.formatMessage(messages.search)}
                    value={searchText}
                    onChange={(e, { value }) => setSearchText(value)}
                    onKeyPress={onSearchKeyPress}
                  />

              </div>
              <Grid columns={12} className='agendaBlock__grid'>
                  <Grid.Column mobile={12} tablet={6} computer={6} widescreen={4} largeScreen={4}>
                    <section id="agenda-calendar" className="d-print-none">
                      <h3 className="visually-hidden"><FormattedMessage id="Calendari d'activitats" defaultMessage="Calendari d'activitats" /></h3>
                      <Calendar
                        localizer={localizer}
                        events={mappedEvents}
                        views={['month']}
                        defaultView="month"
                        min={dayjs().startOf('month').toDate()}
                        max={dayjs().endOf('month').toDate()}
                        formats={{
                          dayHeaderFormat: date => {
                            return dayjs(date).toDate()
                          }
                        }}
                        messages={{
                          next: intl.formatMessage(messages.next),
                          previous: intl.formatMessage(messages.previous),
                          today: intl.formatMessage(messages.today),

                          month: intl.formatMessage(messages.month),
                          week: intl.formatMessage(messages.week),
                          day: intl.formatMessage(messages.day),
                          agenda: intl.formatMessage(messages.agenda),

                          date: intl.formatMessage(messages.date),
                          time: intl.formatMessage(messages.time),
                          event: intl.formatMessage(messages.event),

                          showMore: (total) =>
                            intl.formatMessage(messages.showMore, { count: total }),
                        }}
                        components={calendarComponents}
                        onNavigate={onCalendarMonthChange}
                        />
                    </section>
                  </Grid.Column>
                  <Grid.Column mobile={12} tablet={6} computer={6} widescreen={8} largeScreen={8}>
                    {!isEmpty(events) && (
                      <Grid columns={12} as="ul" className='agendaBlock__list list-unstyled'>
                        {events.slice(0, 2).map((item) => (
                          <Grid.Column mobile={12} tablet={12} computer={12} widescreen={6} largeScreen={6} key={item['@id']} as='li' >
                            <Card fluid as="article" className="eventsItem position-relative">
                              {item.image_field && (
                                <div className="eventsItem__figure">
                                  <PreviewImage
                                    item={item}
                                    alt={item.image_caption ? item.image_caption : ''}
                                    className="eventsItem__img m-0 image"
                                    sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, (max-width: 1599px) 33.33vw, 25vw"
                                  />
                                </div>
                              )}
                              <Card.Content className='eventsItem__text'  >
                                {item.Subject?.length > 0 && ( 
                                  <Card.Meta className="eventsItem__meta mb-3">
                                    {map(item.Subject.slice(0, 1), (item, index) => (
                                      <span key={item} className="newsItem__subject__item" >{getEventSubject(item)}</span>
                                    ))}
                                  </Card.Meta>
                                )}
                                <Card.Header as="h3" className="eventsItem__title">
                                  <UniversalLink href={flattenToAppURL(item['@id'])} className="eventsItem__link stretched-link">{item.title}</UniversalLink>
                                </Card.Header>

                                <ul className="list-unstyled mt-0 d-flex flex-column gap-3 mb-3">
                                  <li className='mb-0'>
                                    <div className="eventsItem__date">{getEventDate(item.start, item.end, item.open_end)}</div>
                                  </li>
                                  {!item.whole_day && (
                                  <li className='d-flex align-items-center mb-0'>
                                    <FontAwesomeIcon className='eventsItem__icon me-2 ' icon={faClock} />
                                    <span suppressHydrationWarning>{formatTime(item.start)}</span>
                                  </li>
                                  )}
                                  {item.location && (
                                  <li className=' align-items-center d-flex mb-0'>
                                    <Icon name='location-outline' className='eventsItem__icon me-2' /> {item.location}
                                  </li>
                                  )}
                                </ul>
                                <div className="text-end mt-auto">
                                  <FontAwesomeIcon icon={faArrowRight} />
                                </div>

                              </Card.Content>
                            </Card>
                          </Grid.Column>

                        ))}
                      </Grid>
                    )}
                  </Grid.Column>
              </Grid>

              {link && 
                <p className="readMoreItem d-print-none">
                  <UniversalLink href={link} className="readMoreItem__btn ui button primary">
                    {data.linkTitle ? (<>{data.linkTitle}</>) : (<FormattedMessage id="See all" defaultMessage="See all"  />)}
                  </UniversalLink>
                </p>
              }

          </Container>
      </section>
    </div>
  );

};


export default injectIntl(View);